import { useState, useEffect, useMemo, useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useEnhancedGameSession } from '../hooks/useEnhancedGameSession'
import { GameSetupScreen } from '../components/GameSetupScreen'
import { GameHeader } from '../components/GameHeader'
import { ChatWidget } from '../components/ChatWidget'
import { GameLayout } from '../components/GameLayout'
import { UserProfileManager } from '../utils/userProfile'
import { GuessWhoAI } from '../utils/ai/guesswho'
import type { AIDifficulty } from '../components/AIModeSelector'
import '../styles/GamePage.css'
import '../styles/GuessWho.css'
import '../styles/UniversalGameRoom.css'
import '../styles/mobile-fixes.css'
import '../styles/mobile-bottom-sheet.css'
import { generateAvatarUrl } from '../utils/avatarGenerator'
import type { Character } from '../types/game'
import {
  GameRoomHeader,
  GameRoomSidebar,
  GameRoomFooter,
  FloatingActionButton
} from '../components/GameRoom'
import type { Message, HistoryItem } from '../components/GameRoom/GameRoomSidebar'
import { useSidebar } from '../hooks/useSidebar'

type Notification = {
  message: string
  type: 'info' | 'success' | 'error'
}

type QAHistoryItem = {
  question: string
  answer: boolean
  asker: 'player' | 'ai'
  timestamp: number
}

// Pool of 48 characters - game randomly selects 24 per session
const CHARACTER_POOL: Character[] = [
  // Original 12
  { id: 1, name: 'Alex', attributes: { gender: 'male', hairColor: 'black', hasGlasses: false, hasHat: false, facialHair: true, hairStyle: 'short' }, eliminated: false },
  { id: 2, name: 'Beth', attributes: { gender: 'female', hairColor: 'blonde', hasGlasses: false, hasHat: false, facialHair: false, hairStyle: 'long' }, eliminated: false },
  { id: 3, name: 'Charlie', attributes: { gender: 'male', hairColor: 'brown', hasGlasses: true, hasHat: false, facialHair: false, hairStyle: 'short' }, eliminated: false },
  { id: 4, name: 'Diana', attributes: { gender: 'female', hairColor: 'red', hasGlasses: false, hasHat: true, facialHair: false, hairStyle: 'medium' }, eliminated: false },
  { id: 5, name: 'Eric', attributes: { gender: 'male', hairColor: 'blonde', hasGlasses: false, hasHat: false, facialHair: true, hairStyle: 'bald' }, eliminated: false },
  { id: 6, name: 'Fiona', attributes: { gender: 'female', hairColor: 'black', hasGlasses: true, hasHat: false, facialHair: false, hairStyle: 'long' }, eliminated: false },
  { id: 7, name: 'George', attributes: { gender: 'male', hairColor: 'gray', hasGlasses: false, hasHat: true, facialHair: true, hairStyle: 'short' }, eliminated: false },
  { id: 8, name: 'Hannah', attributes: { gender: 'female', hairColor: 'brown', hasGlasses: false, hasHat: false, facialHair: false, hairStyle: 'curly' }, eliminated: false },
  { id: 9, name: 'Ian', attributes: { gender: 'male', hairColor: 'red', hasGlasses: true, hasHat: false, facialHair: false, hairStyle: 'short' }, eliminated: false },
  { id: 10, name: 'Julia', attributes: { gender: 'female', hairColor: 'blonde', hasGlasses: false, hasHat: false, facialHair: false, hairStyle: 'short' }, eliminated: false },
  { id: 11, name: 'Kevin', attributes: { gender: 'male', hairColor: 'black', hasGlasses: false, hasHat: false, facialHair: true, hairStyle: 'medium' }, eliminated: false },
  { id: 12, name: 'Laura', attributes: { gender: 'female', hairColor: 'red', hasGlasses: true, hasHat: false, facialHair: false, hairStyle: 'long' }, eliminated: false },

  // Additional 36 characters for variety
  { id: 13, name: 'Marcus', attributes: { gender: 'male', hairColor: 'brown', hasGlasses: false, hasHat: true, facialHair: true, hairStyle: 'medium' }, eliminated: false },
  { id: 14, name: 'Nina', attributes: { gender: 'female', hairColor: 'black', hasGlasses: false, hasHat: false, facialHair: false, hairStyle: 'curly' }, eliminated: false },
  { id: 15, name: 'Oscar', attributes: { gender: 'male', hairColor: 'blonde', hasGlasses: true, hasHat: false, facialHair: false, hairStyle: 'long' }, eliminated: false },
  { id: 16, name: 'Petra', attributes: { gender: 'female', hairColor: 'red', hasGlasses: false, hasHat: true, facialHair: false, hairStyle: 'short' }, eliminated: false },
  { id: 17, name: 'Quinn', attributes: { gender: 'male', hairColor: 'gray', hasGlasses: true, hasHat: false, facialHair: true, hairStyle: 'bald' }, eliminated: false },
  { id: 18, name: 'Rachel', attributes: { gender: 'female', hairColor: 'brown', hasGlasses: true, hasHat: false, facialHair: false, hairStyle: 'medium' }, eliminated: false },
  { id: 19, name: 'Sam', attributes: { gender: 'male', hairColor: 'black', hasGlasses: false, hasHat: true, facialHair: false, hairStyle: 'curly' }, eliminated: false },
  { id: 20, name: 'Tina', attributes: { gender: 'female', hairColor: 'blonde', hasGlasses: true, hasHat: false, facialHair: false, hairStyle: 'long' }, eliminated: false },
  { id: 21, name: 'Umar', attributes: { gender: 'male', hairColor: 'brown', hasGlasses: false, hasHat: false, facialHair: true, hairStyle: 'short' }, eliminated: false },
  { id: 22, name: 'Vera', attributes: { gender: 'female', hairColor: 'red', hasGlasses: false, hasHat: false, facialHair: false, hairStyle: 'curly' }, eliminated: false },
  { id: 23, name: 'Wade', attributes: { gender: 'male', hairColor: 'blonde', hasGlasses: true, hasHat: true, facialHair: false, hairStyle: 'short' }, eliminated: false },
  { id: 24, name: 'Xena', attributes: { gender: 'female', hairColor: 'black', hasGlasses: false, hasHat: true, facialHair: false, hairStyle: 'long' }, eliminated: false },
  { id: 25, name: 'Yale', attributes: { gender: 'male', hairColor: 'gray', hasGlasses: false, hasHat: false, facialHair: true, hairStyle: 'medium' }, eliminated: false },
  { id: 26, name: 'Zara', attributes: { gender: 'female', hairColor: 'brown', hasGlasses: true, hasHat: true, facialHair: false, hairStyle: 'short' }, eliminated: false },
  { id: 27, name: 'Adam', attributes: { gender: 'male', hairColor: 'red', hasGlasses: false, hasHat: false, facialHair: false, hairStyle: 'bald' }, eliminated: false },
  { id: 28, name: 'Bella', attributes: { gender: 'female', hairColor: 'blonde', hasGlasses: false, hasHat: false, facialHair: false, hairStyle: 'medium' }, eliminated: false },
  { id: 29, name: 'Carl', attributes: { gender: 'male', hairColor: 'black', hasGlasses: true, hasHat: false, facialHair: true, hairStyle: 'long' }, eliminated: false },
  { id: 30, name: 'Donna', attributes: { gender: 'female', hairColor: 'brown', hasGlasses: false, hasHat: true, facialHair: false, hairStyle: 'curly' }, eliminated: false },
  { id: 31, name: 'Ethan', attributes: { gender: 'male', hairColor: 'blonde', hasGlasses: false, hasHat: true, facialHair: true, hairStyle: 'short' }, eliminated: false },
  { id: 32, name: 'Faith', attributes: { gender: 'female', hairColor: 'red', hasGlasses: true, hasHat: false, facialHair: false, hairStyle: 'long' }, eliminated: false },
  { id: 33, name: 'Gary', attributes: { gender: 'male', hairColor: 'gray', hasGlasses: true, hasHat: true, facialHair: false, hairStyle: 'short' }, eliminated: false },
  { id: 34, name: 'Holly', attributes: { gender: 'female', hairColor: 'black', hasGlasses: false, hasHat: false, facialHair: false, hairStyle: 'medium' }, eliminated: false },
  { id: 35, name: 'Ivan', attributes: { gender: 'male', hairColor: 'brown', hasGlasses: false, hasHat: false, facialHair: true, hairStyle: 'curly' }, eliminated: false },
  { id: 36, name: 'Jane', attributes: { gender: 'female', hairColor: 'blonde', hasGlasses: true, hasHat: false, facialHair: false, hairStyle: 'short' }, eliminated: false },
  { id: 37, name: 'Kyle', attributes: { gender: 'male', hairColor: 'red', hasGlasses: false, hasHat: true, facialHair: false, hairStyle: 'medium' }, eliminated: false },
  { id: 38, name: 'Lily', attributes: { gender: 'female', hairColor: 'black', hasGlasses: true, hasHat: true, facialHair: false, hairStyle: 'long' }, eliminated: false },
  { id: 39, name: 'Mike', attributes: { gender: 'male', hairColor: 'blonde', hasGlasses: false, hasHat: false, facialHair: false, hairStyle: 'bald' }, eliminated: false },
  { id: 40, name: 'Nora', attributes: { gender: 'female', hairColor: 'brown', hasGlasses: false, hasHat: false, facialHair: false, hairStyle: 'curly' }, eliminated: false },
  { id: 41, name: 'Owen', attributes: { gender: 'male', hairColor: 'gray', hasGlasses: true, hasHat: false, facialHair: true, hairStyle: 'short' }, eliminated: false },
  { id: 42, name: 'Pam', attributes: { gender: 'female', hairColor: 'red', hasGlasses: false, hasHat: true, facialHair: false, hairStyle: 'medium' }, eliminated: false },
  { id: 43, name: 'Ray', attributes: { gender: 'male', hairColor: 'black', hasGlasses: true, hasHat: false, facialHair: false, hairStyle: 'long' }, eliminated: false },
  { id: 44, name: 'Sara', attributes: { gender: 'female', hairColor: 'blonde', hasGlasses: false, hasHat: false, facialHair: false, hairStyle: 'short' }, eliminated: false },
  { id: 45, name: 'Tom', attributes: { gender: 'male', hairColor: 'brown', hasGlasses: false, hasHat: true, facialHair: true, hairStyle: 'medium' }, eliminated: false },
  { id: 46, name: 'Uma', attributes: { gender: 'female', hairColor: 'red', hasGlasses: true, hasHat: false, facialHair: false, hairStyle: 'curly' }, eliminated: false },
  { id: 47, name: 'Vince', attributes: { gender: 'male', hairColor: 'blonde', hasGlasses: false, hasHat: false, facialHair: true, hairStyle: 'bald' }, eliminated: false },
  { id: 48, name: 'Wendy', attributes: { gender: 'female', hairColor: 'black', hasGlasses: true, hasHat: true, facialHair: false, hairStyle: 'long' }, eliminated: false },
]

// Helper function to randomly select N characters from the pool
const selectRandomCharacters = (count: number = 24): Character[] => {
  const shuffled = [...CHARACTER_POOL].sort(() => Math.random() - 0.5)
  return shuffled.slice(0, count).map(char => ({ ...char, eliminated: false }))
}

type GameMode = 'select' | 'local' | 'network' | 'ai'
type GamePhase = 'setup' | 'lobby' | 'waiting' | 'playing' | 'finished'

export default function GuessWho() {
  const [searchParams] = useSearchParams()
  const joinCode = searchParams.get('join')

  // Game mode selection
  const [gameMode, setGameMode] = useState<GameMode>('select')
  const [phase, setPhase] = useState<GamePhase>('setup')

  // AI state
  const [aiDifficulty, setAiDifficulty] = useState<AIDifficulty>('medium')
  const [aiInstance, setAiInstance] = useState<GuessWhoAI | null>(null)
  const [showDifficulty, setShowDifficulty] = useState(false)
  const [aiQuestion, setAiQuestion] = useState<string | null>(null)
  const [playerQuestion, setPlayerQuestion] = useState<string>('')
  const [showPlayerQuestionInput, setShowPlayerQuestionInput] = useState(false)
  const [qaHistory, setQaHistory] = useState<QAHistoryItem[]>([])
  const [showQaHistory, setShowQaHistory] = useState(false)

  // Log AI question for debugging (will be displayed in UI later)
  useEffect(() => {
    if (aiQuestion) {
      console.log('[GuessWho AI]', aiQuestion)
    }
  }, [aiQuestion])

  // Settings - will auto-select 24 random characters when game starts
  const [selectedCharacters, setSelectedCharacters] = useState<Character[]>([])

  // Auto-fill from profile
  const userProfile = UserProfileManager.get()

  // Local game state
  const [currentPlayer, setCurrentPlayer] = useState<1 | 2>(1)
  const [player1Name, setPlayer1Name] = useState(userProfile?.name || 'Player 1')
  const [player2Name, setPlayer2Name] = useState('Player 2')
  const [player1Characters, setPlayer1Characters] = useState<Character[]>([])
  const [player2Characters, setPlayer2Characters] = useState<Character[]>([])
  const [player1Mystery, setPlayer1Mystery] = useState<Character | null>(null)
  const [player2Mystery, setPlayer2Mystery] = useState<Character | null>(null)
  const [, setHasAskedQuestion] = useState(false) // Track if current player asked their question

  // Network game state
  const [playerName, setPlayerName] = useState(userProfile?.name || '')
  const [playerNumber, setPlayerNumber] = useState<1 | 2>(1)
  const [myCharacters, setMyCharacters] = useState<Character[]>([])
  const [mysteryCharacter, setMysteryCharacter] = useState<Character | null>(null)
  const [sessionCode, setSessionCode] = useState<string | undefined>(undefined)
  const [currentTurn, setCurrentTurn] = useState<1 | 2>(1) // Track whose turn it is in network games

  // Notification state
  const [notification, setNotification] = useState<Notification | null>(null)

  // Confirmation dialog state
  const [confirmDialog, setConfirmDialog] = useState<{
    show: boolean
    message: string
    characterId?: number
    onConfirm: () => void
  }>({ show: false, message: '', onConfirm: () => {} })

  // Universal sidebar state
  const [chatMessages, setChatMessages] = useState<Message[]>([])
  const [historyItems, setHistoryItems] = useState<HistoryItem[]>([])
  const sidebar = useSidebar()

  const {
    session,
    gameState,
    loading,
    error,
    createSession,
    joinSession,
    sendMove,
    voice,
    onMessage,
  } = useEnhancedGameSession({
    sessionCode,
    playerNumber,
    playerName,
  })

  // Helper to add Q&A to messages (AI mode)
  const addAIQuestion = useCallback((question: string) => {
    const newMessage: Message = {
      sender: 'ai',
      content: question,
      timestamp: Date.now()
    }
    setChatMessages(prev => [...prev, newMessage])
  }, [])

  const addPlayerQuestion = useCallback((question: string) => {
    const newMessage: Message = {
      sender: 'you',
      content: question,
      timestamp: Date.now()
    }
    setChatMessages(prev => [...prev, newMessage])
  }, [])

  const addAIAnswer = useCallback((answer: string) => {
    const newMessage: Message = {
      sender: 'ai',
      content: answer,
      timestamp: Date.now()
    }
    setChatMessages(prev => [...prev, newMessage])
  }, [])

  const addSystemMessage = useCallback((message: string) => {
    const newMessage: Message = {
      sender: 'system',
      content: message,
      timestamp: Date.now()
    }
    setChatMessages(prev => [...prev, newMessage])
  }, [])

  // Persist session to localStorage for rejoin capability
  useEffect(() => {
    if (sessionCode && playerNumber && playerName) {
      const sessionData = {
        code: sessionCode,
        playerNumber,
        playerName,
        game: 'guess-who',
        timestamp: Date.now(),
      }
      localStorage.setItem('zgames_active_session', JSON.stringify(sessionData))
      console.log('[GuessWho] Session saved to localStorage for rejoin')
    }
  }, [sessionCode, playerNumber, playerName])

  // Auto-join if join code in URL or restore from localStorage
  useEffect(() => {
    if (joinCode && gameMode === 'select') {
      console.log('[GuessWho] Auto-joining from URL:', joinCode)
      setGameMode('network')
      setPhase('lobby')
      return
    }

    // Check for active session in localStorage (rejoin capability)
    const savedSession = localStorage.getItem('zgames_active_session')
    if (savedSession && gameMode === 'select') {
      try {
        const sessionData = JSON.parse(savedSession)

        // Only auto-rejoin if session is less than 2 hours old and is GuessWho
        const sessionAge = Date.now() - sessionData.timestamp
        const twoHours = 2 * 60 * 60 * 1000

        if (sessionData.game === 'guess-who' && sessionAge < twoHours) {
          console.log('[GuessWho] Found active session, showing rejoin option')
          showNotification(`You have an active game session. Click "Rejoin Game" to continue.`, 'info')
          // User can manually rejoin via the UI
        } else {
          // Clean up old session
          localStorage.removeItem('zgames_active_session')
        }
      } catch (err) {
        console.error('[GuessWho] Failed to parse saved session:', err)
        localStorage.removeItem('zgames_active_session')
      }
    }
  }, [joinCode, gameMode])

  // Auto-dismiss notification after 5 seconds
  useEffect(() => {
    if (notification) {
      const timer = setTimeout(() => setNotification(null), 5000)
      return () => clearTimeout(timer)
    }
  }, [notification])

  const showNotification = (message: string, type: Notification['type'] = 'info') => {
    setNotification({ message, type })
  }

  const handleModeSelect = (mode: GameMode) => {
    if (mode === 'ai') {
      setShowDifficulty(true)
      return
    }
    setGameMode(mode)
    setShowDifficulty(false)
    setPhase(mode === 'local' ? 'setup' : 'lobby')
  }

  const handleDifficultySelect = (difficulty: AIDifficulty) => {
    setAiDifficulty(difficulty)
    setGameMode('ai')
    setShowDifficulty(false)
    setPhase('setup')
  }

  const startLocalGame = () => {
    // Auto-select 24 random characters for this game
    const gameCharacters = selectRandomCharacters(24)
    setSelectedCharacters(gameCharacters)

    const shuffled = [...gameCharacters].sort(() => Math.random() - 0.5)
    const p1Mystery = shuffled[0]
    const p2Mystery = shuffled[1]

    setPlayer1Mystery(p1Mystery)
    setPlayer2Mystery(p2Mystery)
    setPlayer1Characters(gameCharacters.map(c => ({ ...c, eliminated: false })))
    setPlayer2Characters(gameCharacters.map(c => ({ ...c, eliminated: false })))
    setPhase('playing')
    setCurrentPlayer(1)

    // Initialize AI if in AI mode
    if (gameMode === 'ai') {
      const ai = new GuessWhoAI(gameCharacters)
      setAiInstance(ai)
      setPlayer2Name(`Computer (${aiDifficulty.charAt(0).toUpperCase() + aiDifficulty.slice(1)})`)
    }

    // Reset Q&A history
    setQaHistory([])

    // Track game started
    UserProfileManager.incrementGamesPlayed()
  }

  const handleCreateNetworkGame = async () => {
    if (!playerName.trim()) {
      showNotification('Please enter your nickname', 'error')
      return
    }

    try {
      // Auto-select 24 random characters for this network game
      const gameCharacters = selectRandomCharacters(24)
      setSelectedCharacters(gameCharacters)

      // Create session with character list in initial state
      const newSession = await createSession('guess-who', playerName.trim())
      const code = newSession.session_code
      setSessionCode(code)
      setPlayerNumber(1)

      // Initialize Player 1's game with the characters
      initializeNetworkGame(1, gameCharacters)

      // Send initial game state with character list to Player 2
      // IMPORTANT: Use the code variable directly, not sessionCode state (which hasn't updated yet)
      console.log('[GuessWho] Player 1: Sending character list to database', {
        code,
        characterCount: gameCharacters.length,
        firstChar: gameCharacters[0]?.name
      })

      // Manually call the API since sendMove won't have the updated sessionCode yet
      await fetch(`/api/sessions/${code}/move`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          playerNumber: 1,
          moveData: { type: 'game_state', characters: gameCharacters },
          gameState: { characters: gameCharacters, currentTurn: 1 }
        })
      })

      setPhase('waiting')
      // Code is shown on waiting screen - no notification needed
    } catch (err) {
      showNotification(err instanceof Error ? err.message : 'Failed to create game', 'error')
    }
  }

  const handleJoinNetworkGame = async (code?: string) => {
    const targetCode = code || joinCode
    if (!targetCode) {
      showNotification('Please enter a session code', 'error')
      return
    }

    if (!playerName.trim()) {
      showNotification('Please enter your nickname', 'error')
      return
    }

    try {
      await joinSession(targetCode, playerName.trim())
      setSessionCode(targetCode)
      setPlayerNumber(2)
      // Don't initialize yet - wait for game state sync via useEffect
      setPhase('playing')
      // Join successful - game starts automatically, characters will sync from host
    } catch (err) {
      showNotification(err instanceof Error ? err.message : 'Failed to join game', 'error')
    }
  }

  const initializeNetworkGame = (pNum: 1 | 2, characters?: Character[]) => {
    const chars = characters || selectedCharacters
    const shuffled = [...chars].sort(() => Math.random() - 0.5)
    const mystery = shuffled[pNum - 1]
    setMysteryCharacter(mystery)
    setMyCharacters(chars.map(c => ({ ...c, eliminated: false })))
  }

  // Watch for player 2 joining and auto-connect voice
  useEffect(() => {
    if (session && phase === 'waiting' && session.player2_name) {
      setPhase('playing')
      // Auto-connect voice chat when Player 2 joins
      if (!voice.isConnected && voice.connect) {
        setTimeout(() => {
          voice.connect()
        }, 1000) // Small delay to ensure both players are ready
      }
    }
  }, [session, phase, voice])

  // Auto-connect voice when joining as Player 2
  useEffect(() => {
    if (gameMode === 'network' && phase === 'playing' && playerNumber === 2 && !voice.isConnected && voice.connect) {
      setTimeout(() => {
        voice.connect()
      }, 1500) // Small delay after joining
    }
  }, [gameMode, phase, playerNumber, voice])

  // AI turn logic
  useEffect(() => {
    if (gameMode !== 'ai') return
    if (phase !== 'playing') return
    if (currentPlayer !== 2) return // AI is player 2
    if (!aiInstance) return
    if (aiQuestion) return // Already asked a question, waiting for response

    // Delay AI move for better UX
    const timer = setTimeout(() => {
      const question = aiInstance.askQuestion()
      setAiQuestion(question)
      // Don't show notification, show dialog instead
    }, 800)

    return () => clearTimeout(timer)
  }, [gameMode, phase, currentPlayer, aiInstance, aiQuestion])

  // Sync current turn and character list from session state
  useEffect(() => {
    console.log('[GuessWho] Sync useEffect triggered', {
      hasGameState: !!gameState,
      gameMode,
      playerNumber,
      gameStateKeys: gameState ? Object.keys(gameState) : [],
      hasCharacters: gameState?.characters ? true : false,
      selectedCharsLength: selectedCharacters.length
    })

    if (!gameState || gameMode !== 'network') return

    // Sync turn
    if (gameState.currentTurn) {
      setCurrentTurn(gameState.currentTurn)
    }

    // Sync character list (Player 2 receives from Player 1)
    if (gameState.characters && playerNumber === 2 && selectedCharacters.length === 0) {
      console.log('[GuessWho] Player 2: Syncing character list from host', gameState.characters)
      setSelectedCharacters(gameState.characters)
      // Re-initialize with synced characters
      const shuffled = [...gameState.characters].sort(() => Math.random() - 0.5)
      const mystery = shuffled[1] // Player 2's mystery
      setMysteryCharacter(mystery)
      setMyCharacters(gameState.characters.map((c: Character) => ({ ...c, eliminated: false })))
      console.log('[GuessWho] Player 2: Initialized with', gameState.characters.length, 'characters')
    }

    // Sync my characters (they might be updated by opponent's moves)
    const myKey = `player${playerNumber}Characters`
    if (gameState[myKey]) {
      setMyCharacters(gameState[myKey])
    }
  }, [gameState, gameMode, playerNumber, selectedCharacters.length])

  // Handle real-time WebRTC messages
  useEffect(() => {
    if (gameMode !== 'network') return

    const unsubscribe = onMessage('move', (data) => {
      // Extract moveData from PeerJS message wrapper
      const moveData = data.moveData || data

      if (moveData.type === 'turn_change') {
        // Handle turn change
        console.log('[GuessWho] Turn changed to:', moveData.nextTurn)
        setCurrentTurn(moveData.nextTurn)
      } else if (moveData.type === 'guess' && moveData.characterId && moveData.guesserNumber) {
        // Check if this is MY guess or opponent's guess
        if (moveData.guesserNumber === playerNumber) {
          // This is MY guess being echoed back - ignore it
          // (The guesser waits for guess_result instead)
          return
        }

        // Opponent made a guess - check if they got it right!
        const guessedChar = selectedCharacters.find(c => c.id === moveData.characterId)
        const isCorrect = mysteryCharacter?.id === moveData.characterId

        if (isCorrect) {
          // Correct guess - opponent wins!
          showNotification(`Opponent guessed ${guessedChar?.name} - CORRECT! They win!`, 'error')
          setTimeout(() => setPhase('finished'), 2000)
          // Send confirmation
          sendMove(
            { type: 'guess_result', correct: true, winner: moveData.guesserNumber },
            { winner: moveData.guesserNumber, phase: 'finished' }
          )
          // No win for user (opponent won)
        } else {
          // OFFICIAL RULE: Wrong guess = immediate loss for guesser (YOU win!)
          showNotification(`Opponent guessed ${guessedChar?.name} - WRONG! You win!`, 'success')
          setTimeout(() => setPhase('finished'), 2000)
          // Send confirmation
          sendMove(
            { type: 'guess_result', correct: false, winner: playerNumber },
            { winner: playerNumber, phase: 'finished' }
          )
          // Track network win
          UserProfileManager.incrementWins()
        }
      } else if (moveData.type === 'guess_result') {
        // Received result of our guess
        // Close confirmation dialog
        setConfirmDialog({ show: false, message: '', onConfirm: () => {} })

        if (moveData.correct) {
          // Correct - we win!
          showNotification('Your guess was CORRECT! You win!', 'success')
          setTimeout(() => setPhase('finished'), 2000)
          // Track network game win (only if winner is us, prevent double-counting)
          if (moveData.winner === playerNumber) {
            UserProfileManager.incrementWins()
          }
        } else {
          // OFFICIAL RULE: Wrong guess = immediate loss for guesser
          showNotification('Your guess was WRONG! You lose!', 'error')
          setTimeout(() => setPhase('finished'), 2000)
          // No win recorded (opponent won)
        }
      }
    })

    const unsubscribeChat = onMessage('chat', (data) => {
      // Add chat message to unified messages feed
      console.log('[GuessWho] Chat message:', data.message)
      const newMessage: Message = {
        sender: data.playerNumber === playerNumber ? 'you' : 'opponent',
        content: data.message,
        timestamp: Date.now()
      }
      setChatMessages(prev => [...prev, newMessage])
    })

    return () => {
      unsubscribe()
      unsubscribeChat()
    }
  }, [gameMode, onMessage, mysteryCharacter, selectedCharacters, playerNumber, sendMove])

  const eliminateCharacterLocal = (characterId: number) => {
    if (currentPlayer === 1) {
      setPlayer1Characters(prev =>
        prev.map(c => c.id === characterId ? { ...c, eliminated: !c.eliminated } : c)
      )
    } else {
      setPlayer2Characters(prev =>
        prev.map(c => c.id === characterId ? { ...c, eliminated: !c.eliminated } : c)
      )
    }
  }

  const eliminateCharacterNetwork = async (characterId: number) => {
    // Eliminating characters does NOT require your turn - you can do it anytime
    // This is just local tracking, no need to send to opponent
    const updated = myCharacters.map(c =>
      c.id === characterId ? { ...c, eliminated: !c.eliminated } : c
    )
    setMyCharacters(updated)
  }

  const switchTurn = () => {
    setCurrentPlayer(prev => prev === 1 ? 2 : 1)
    setPlayerQuestion('') // Clear question input when switching turns
    setHasAskedQuestion(false) // Reset question state for next player
  }

  const handleAiResponse = (answer: boolean) => {
    if (!aiInstance || !aiQuestion) return

    // Add AI's question and player's response to chat
    const answerText = answer ? '✓ Yes' : '✗ No'
    addAIQuestion(aiQuestion)
    setTimeout(() => {
      addSystemMessage(`You answered: ${answerText}`)
    }, 300)

    // Track Q&A history
    setQaHistory(prev => [...prev, {
      question: aiQuestion,
      answer,
      asker: 'ai',
      timestamp: Date.now()
    }])

    // Add to history items
    setHistoryItems(prev => [...prev, {
      type: 'question',
      content: `AI: "${aiQuestion}"`,
      timestamp: Date.now()
    }, {
      type: 'answer',
      content: `You: ${answerText}`,
      timestamp: Date.now()
    }])

    // Process AI's question and player's answer
    aiInstance.processAnswer(aiQuestion, answer)

    // Check if AI should make a guess (1 character remaining)
    const guess = aiInstance.makeGuess()
    if (guess) {
      // AI makes final guess
      const isCorrect = player1Mystery?.id === guess.id
      if (isCorrect) {
        addSystemMessage(`AI guesses ${guess.name} - CORRECT! AI wins!`)
        showNotification(`AI guesses ${guess.name} - CORRECT! AI wins!`, 'error')
        setTimeout(() => setPhase('finished'), 2000)
      } else {
        addSystemMessage(`AI guesses ${guess.name} - WRONG! You win!`)
        showNotification(`AI guesses ${guess.name} - WRONG! You win!`, 'success')
        setTimeout(() => setPhase('finished'), 2000)
        UserProfileManager.incrementWins()
      }
      setAiQuestion(null)
      return
    }

    // Clear the question
    setAiQuestion(null)

    // AI's turn is done, switch back to player
    setCurrentPlayer(1)
    setHasAskedQuestion(false) // Reset for player's next turn

    showNotification(`You answered: ${answer ? 'Yes' : 'No'}. Your turn!`, 'info')
  }

  const handlePlayerQuestion = () => {
    if (!aiInstance || !player2Mystery || !playerQuestion.trim()) return

    // Simple attribute checking based on the question
    const question = playerQuestion.toLowerCase().trim()
    let answer = false

    // Check various attributes
    if (question.includes('male') || question.includes('man') || question.includes('boy')) {
      answer = player2Mystery.attributes.gender === 'male'
    } else if (question.includes('female') || question.includes('woman') || question.includes('girl')) {
      answer = player2Mystery.attributes.gender === 'female'
    } else if (question.includes('glasses') || question.includes('spectacles')) {
      answer = player2Mystery.attributes.hasGlasses === true
    } else if (question.includes('hat') || question.includes('cap')) {
      answer = player2Mystery.attributes.hasHat === true
    } else if (question.includes('facial hair') || question.includes('beard') || question.includes('mustache')) {
      answer = player2Mystery.attributes.facialHair === true
    } else if (question.includes('black hair')) {
      answer = player2Mystery.attributes.hairColor === 'black'
    } else if (question.includes('blonde') || question.includes('blond')) {
      answer = player2Mystery.attributes.hairColor === 'blonde'
    } else if (question.includes('brown hair')) {
      answer = player2Mystery.attributes.hairColor === 'brown'
    } else if (question.includes('red hair') || question.includes('ginger')) {
      answer = player2Mystery.attributes.hairColor === 'red'
    } else if (question.includes('gray') || question.includes('grey')) {
      answer = player2Mystery.attributes.hairColor === 'gray'
    } else if (question.includes('bald')) {
      answer = player2Mystery.attributes.hairStyle === 'bald'
    } else if (question.includes('long hair')) {
      answer = player2Mystery.attributes.hairStyle === 'long'
    } else if (question.includes('short hair')) {
      answer = player2Mystery.attributes.hairStyle === 'short'
    } else if (question.includes('curly')) {
      answer = player2Mystery.attributes.hairStyle === 'curly'
    } else {
      showNotification('Please ask a clearer yes/no question about attributes (gender, glasses, hat, hair color/style, facial hair)', 'error')
      return
    }

    // Add to chat messages (new unified system)
    addPlayerQuestion(playerQuestion.trim())
    const answerText = answer ? '✓ Yes' : '✗ No'
    addAIAnswer(answerText)

    // Track Q&A history
    setQaHistory(prev => [...prev, {
      question: playerQuestion.trim(),
      answer,
      asker: 'player',
      timestamp: Date.now()
    }])

    // Add to history items for sidebar
    setHistoryItems(prev => [...prev, {
      type: 'question',
      content: `You: "${playerQuestion.trim()}"`,
      timestamp: Date.now()
    }, {
      type: 'answer',
      content: `AI: ${answerText}`,
      timestamp: Date.now()
    }])

    showNotification(`AI answers: ${answerText}. Now eliminate characters based on this answer.`, 'info')
    setPlayerQuestion('')
    setShowPlayerQuestionInput(false)
    setHasAskedQuestion(true) // Mark that player asked their one question

    // Turn auto-ends after 5 seconds (time to eliminate characters)
    setTimeout(() => {
      switchTurn()
      showNotification("Turn ended. AI's turn now!", 'info')
    }, 5000)
  }

  // Handler for sidebar message sending (AI mode)
  const handleSidebarMessage = useCallback(async (message: string) => {
    if (gameMode !== 'ai') {
      // Network mode - send via session
      if (sessionCode) {
        await sendMove({ type: 'chat', message })
      }
      return
    }

    // AI mode - treat as player question
    if (!aiInstance || !player2Mystery) return

    const question = message.toLowerCase().trim()
    let answer = false

    // Check various attributes (same logic as handlePlayerQuestion)
    if (question.includes('male') || question.includes('man') || question.includes('boy')) {
      answer = player2Mystery.attributes.gender === 'male'
    } else if (question.includes('female') || question.includes('woman') || question.includes('girl')) {
      answer = player2Mystery.attributes.gender === 'female'
    } else if (question.includes('glasses') || question.includes('spectacles')) {
      answer = player2Mystery.attributes.hasGlasses === true
    } else if (question.includes('hat') || question.includes('cap')) {
      answer = player2Mystery.attributes.hasHat === true
    } else if (question.includes('facial hair') || question.includes('beard') || question.includes('mustache')) {
      answer = player2Mystery.attributes.facialHair === true
    } else if (question.includes('black hair')) {
      answer = player2Mystery.attributes.hairColor === 'black'
    } else if (question.includes('blonde') || question.includes('blond')) {
      answer = player2Mystery.attributes.hairColor === 'blonde'
    } else if (question.includes('brown hair')) {
      answer = player2Mystery.attributes.hairColor === 'brown'
    } else if (question.includes('red hair') || question.includes('ginger')) {
      answer = player2Mystery.attributes.hairColor === 'red'
    } else if (question.includes('gray') || question.includes('grey')) {
      answer = player2Mystery.attributes.hairColor === 'gray'
    } else if (question.includes('bald')) {
      answer = player2Mystery.attributes.hairStyle === 'bald'
    } else if (question.includes('long hair')) {
      answer = player2Mystery.attributes.hairStyle === 'long'
    } else if (question.includes('short hair')) {
      answer = player2Mystery.attributes.hairStyle === 'short'
    } else if (question.includes('curly')) {
      answer = player2Mystery.attributes.hairStyle === 'curly'
    } else {
      addSystemMessage('Please ask a clearer yes/no question about attributes (gender, glasses, hat, hair color/style, facial hair)')
      return
    }

    // Add to chat messages
    addPlayerQuestion(message)
    const answerText = answer ? '✓ Yes' : '✗ No'
    setTimeout(() => {
      addAIAnswer(answerText)
    }, 500) // Small delay for better UX

    // Track Q&A history
    setQaHistory(prev => [...prev, {
      question: message,
      answer,
      asker: 'player',
      timestamp: Date.now()
    }])

    // Add to history items
    setHistoryItems(prev => [...prev, {
      type: 'question',
      content: `You: "${message}"`,
      timestamp: Date.now()
    }, {
      type: 'answer',
      content: `AI: ${answerText}`,
      timestamp: Date.now()
    }])

    setHasAskedQuestion(true)

    // Turn auto-ends after 5 seconds
    setTimeout(() => {
      switchTurn()
      addSystemMessage("Turn ended. AI's turn now!")
    }, 5000)
  }, [gameMode, aiInstance, player2Mystery, sessionCode, sendMove, addPlayerQuestion, addAIAnswer, addSystemMessage])

  const switchTurnNetwork = async () => {
    const nextTurn = currentTurn === 1 ? 2 : 1
    setCurrentTurn(nextTurn)
    await sendMove(
      { type: 'turn_change', nextTurn },
      { currentTurn: nextTurn }
    )
  }

  const makeGuessLocal = (characterId: number) => {
    const mysteryChar = currentPlayer === 1 ? player2Mystery : player1Mystery
    const currentPlayerName = currentPlayer === 1 ? player1Name : player2Name
    const opponentName = currentPlayer === 1 ? player2Name : player1Name

    if (mysteryChar?.id === characterId) {
      // Correct guess - current player wins!
      showNotification(`${currentPlayerName} wins! The mystery character was ${mysteryChar.name}!`, 'success')
      setTimeout(() => setPhase('finished'), 2000)

      // Track win if current player is Player 1 (user)
      if (currentPlayer === 1) {
        UserProfileManager.incrementWins()
      }
    } else {
      // OFFICIAL RULE: Wrong guess = immediate loss!
      const guessedChar = selectedCharacters.find(c => c.id === characterId)
      showNotification(`${currentPlayerName} guessed ${guessedChar?.name} - WRONG! ${opponentName} wins!`, 'error')
      setTimeout(() => setPhase('finished'), 2000)

      // Track win if opponent is Player 1 (user won because opponent guessed wrong)
      if (currentPlayer === 2 && gameMode === 'ai') {
        UserProfileManager.incrementWins()
      }
    }
  }

  const makeGuessNetwork = async (characterId: number) => {
    if (!mysteryCharacter) return

    // Check if it's your turn
    if (currentTurn !== playerNumber) {
      showNotification("It's not your turn to guess!", 'error')
      return
    }

    const guessedChar = selectedCharacters.find(c => c.id === characterId)

    setConfirmDialog({
      show: true,
      message: `⚠️ Final Guess: Is your opponent's character ${guessedChar?.name}? (Wrong guess = you lose immediately!)`,
      characterId,
      onConfirm: async () => {
        // Send the guess to opponent for confirmation
        await sendMove(
          { type: 'guess', characterId, guesserNumber: playerNumber },
          { pendingGuess: { characterId, guesserNumber: playerNumber } }
        )
        setConfirmDialog({ show: false, message: '', onConfirm: () => {} })
        showNotification('Guess submitted! Waiting for opponent to reveal...', 'info')
      }
    })
  }


  // Memoize dialog components to prevent re-creation on every render
  // This fixes the bug where typing in inputs causes the dialog to re-render and lose focus
  const NotificationComponent = useMemo(() => {
    if (!notification) return null

    return (
      <div className={`notification notification-${notification.type}`}>
        {notification.message}
        <button className="notification-close" onClick={() => setNotification(null)}>×</button>
      </div>
    )
  }, [notification])

  const ConfirmDialog = useMemo(() => {
    if (!confirmDialog.show) return null

    return (
      <div className="modal-overlay">
        <div className="confirm-dialog">
          <h3>Make Your Guess</h3>
          <p>{confirmDialog.message}</p>
          <div className="confirm-actions">
            <button
              className="confirm-btn yes"
              onClick={confirmDialog.onConfirm}
            >
              Yes, that's my guess!
            </button>
            <button
              className="confirm-btn no"
              onClick={() => setConfirmDialog({ show: false, message: '', onConfirm: () => {} })}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    )
  }, [confirmDialog])

  const AIQuestionDialog = useMemo(() => {
    if (!aiQuestion || gameMode !== 'ai') return null

    return (
      <div className="modal-overlay ai-question-overlay">
        <div className="ai-question-dialog">
          <div className="ai-icon">🤖</div>
          <h3>AI Asks:</h3>
          <p className="ai-question-text">{aiQuestion}</p>
          <div className="ai-response-actions">
            <button
              className="ai-response-btn yes"
              onClick={() => handleAiResponse(true)}
            >
              ✓ Yes
            </button>
            <button
              className="ai-response-btn no"
              onClick={() => handleAiResponse(false)}
            >
              ✗ No
            </button>
          </div>
        </div>
      </div>
    )
  }, [aiQuestion, gameMode])

  const PlayerQuestionDialog = useMemo(() => {
    if (!showPlayerQuestionInput || gameMode !== 'ai') return null

    return (
      <div className="modal-overlay ai-question-overlay">
        <div className="ai-question-dialog">
          <div className="ai-icon">🎤</div>
          <h3>Ask Your Question:</h3>
          <div style={{ marginBottom: '16px', fontSize: '14px', color: 'rgba(255,255,255,0.8)' }}>
            Examples: "Does the person wear glasses?", "Is it a male?", "Does the person have black hair?"
          </div>
          <input
            type="text"
            value={playerQuestion}
            onChange={(e) => setPlayerQuestion(e.target.value)}
            placeholder="Type your yes/no question..."
            autoFocus
            onKeyDown={(e) => {
              if (e.key === 'Enter') handlePlayerQuestion()
            }}
            style={{
              width: '100%',
              padding: '14px',
              fontSize: '16px',
              borderRadius: '12px',
              border: '2px solid rgba(255,255,255,0.3)',
              background: 'rgba(255,255,255,0.95)',
              color: '#000',
              marginBottom: '16px',
              outline: 'none'
            }}
          />
          <div className="ai-response-actions">
            <button
              className="ai-response-btn yes"
              onClick={handlePlayerQuestion}
              disabled={!playerQuestion.trim()}
              style={{ opacity: playerQuestion.trim() ? 1 : 0.5 }}
            >
              ✓ Ask
            </button>
            <button
              className="ai-response-btn no"
              onClick={() => {
                setShowPlayerQuestionInput(false)
                setPlayerQuestion('')
              }}
            >
              ✗ Cancel
            </button>
          </div>
        </div>
      </div>
    )
  }, [showPlayerQuestionInput, gameMode, playerQuestion])

  const QAHistoryPopup = useMemo(() => {
    if (!showQaHistory || gameMode !== 'ai') return null

    return (
      <div className="modal-overlay qa-history-overlay">
        <div className="qa-history-dialog">
          <div className="qa-history-header">
            <h3>📜 Q&A History</h3>
            <button className="qa-history-close" onClick={() => setShowQaHistory(false)}>×</button>
          </div>
          {qaHistory.length === 0 ? (
            <div className="qa-history-empty">
              No questions asked yet. Start asking to build your history!
            </div>
          ) : (
            <div className="qa-history-list">
              {qaHistory.map((item, index) => (
                <div key={index} className="qa-history-item">
                  <div className="qa-history-question">
                    <strong>{item.asker === 'player' ? 'You:' : 'AI:'}</strong>
                    <span>{item.question}</span>
                  </div>
                  <div className={`qa-history-answer ${item.answer ? 'yes' : 'no'}`}>
                    <strong>→</strong>
                    <span>{item.answer ? '✓ Yes' : '✗ No'}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    )
  }, [showQaHistory, gameMode, qaHistory])

  // Mode selection screen
  if (gameMode === 'select') {
    return (
      <>
        {NotificationComponent}
        {ConfirmDialog}
        {AIQuestionDialog}
        <GameSetupScreen
          gameTitle="Guess Who?"
          gameIcon="🎭"
          gameBanner="var(--gradient-purple)"
          supportedModes={['local', 'network', 'ai']}
          onModeSelect={handleModeSelect}
        >
          {/* AI Difficulty selector shown after AI mode selected */}
          {showDifficulty && (
            <div className="difficulty-selector">
              <h3>Select AI Difficulty</h3>
              <div className="difficulty-options">
                {(['easy', 'medium', 'hard'] as AIDifficulty[]).map((diff) => (
                  <button
                    key={diff}
                    className="btn btn-secondary"
                    onClick={() => handleDifficultySelect(diff)}
                  >
                    {diff.charAt(0).toUpperCase() + diff.slice(1)}
                  </button>
                ))}
              </div>
            </div>
          )}
        </GameSetupScreen>
      </>
    )
  }

  // Local/AI game setup
  if ((gameMode === 'local' || gameMode === 'ai') && phase === 'setup') {
    return (
      <>
        {NotificationComponent}
        {ConfirmDialog}
        {AIQuestionDialog}
        <GameSetupScreen
          gameTitle={`Guess Who? - ${gameMode === 'ai' ? `AI (${aiDifficulty})` : 'Local'}`}
          gameIcon="🎭"
          gameBanner={gameMode === 'ai' ? 'var(--gradient-orange)' : 'var(--gradient-blue)'}
          supportedModes={[]}
          onModeSelect={() => {}}
          onBack={() => setGameMode('select')}
        >
          <div className="setup-form">
            <h3>{gameMode === 'ai' ? 'Your Name' : 'Player Names'}</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '24px' }}>
              <input
                type="text"
                className="input"
                placeholder={gameMode === 'ai' ? 'Your nickname' : 'Player 1 nickname'}
                value={player1Name}
                onChange={(e) => setPlayer1Name(e.target.value)}
              />
              {gameMode === 'local' && (
                <input
                  type="text"
                  className="input"
                  placeholder="Player 2 nickname"
                  value={player2Name}
                  onChange={(e) => setPlayer2Name(e.target.value)}
                />
              )}
            </div>
            <button className="btn btn-primary btn-lg" onClick={startLocalGame}>
              Start Game
            </button>
          </div>
        </GameSetupScreen>
      </>
    )
  }

  // Network lobby
  if (gameMode === 'network' && phase === 'lobby') {
    return (
      <>
        {NotificationComponent}
        {ConfirmDialog}
        {AIQuestionDialog}
        <GameSetupScreen
          gameTitle="Guess Who? - Online"
          gameIcon="🎭"
          gameBanner="var(--gradient-purple)"
          supportedModes={[]}
          onModeSelect={() => {}}
          onBack={() => setGameMode('select')}
        >
          <div className="setup-form">
            <h3>Your Nickname</h3>
            <input
              type="text"
              className="input"
              placeholder="Enter your nickname"
              value={playerName}
              onChange={(e) => setPlayerName(e.target.value)}
              maxLength={20}
            />

            <button
              className="btn btn-primary btn-lg"
              onClick={handleCreateNetworkGame}
              disabled={loading || !playerName.trim()}
              style={{ marginTop: '16px' }}
            >
              {loading ? 'Creating...' : 'Create Game'}
            </button>

            <div style={{ margin: '30px 0', textAlign: 'center', position: 'relative' }}>
              <div style={{ borderTop: '1px solid rgba(255,255,255,0.2)', position: 'absolute', top: '50%', left: 0, right: 0 }}></div>
              <span style={{ background: 'var(--bg-primary)', padding: '0 20px', position: 'relative', color: 'var(--text-secondary)', fontSize: '14px' }}>or join with code</span>
            </div>

            <div style={{ display: 'flex', gap: '10px' }}>
              <input
                type="text"
                className="input"
                placeholder="ABC123"
                defaultValue={joinCode || ''}
                id="join-code-input"
                maxLength={6}
                style={{ flex: 1, textTransform: 'uppercase' }}
              />
              <button
                onClick={() => {
                  const input = document.getElementById('join-code-input') as HTMLInputElement
                  handleJoinNetworkGame(input.value.toUpperCase())
                }}
                disabled={loading || !playerName.trim()}
                className="btn btn-secondary"
              >
                {loading ? 'Joining...' : 'Join'}
              </button>
            </div>

            {error && (
              <div className="error-message" style={{ marginTop: '20px', padding: '15px', background: 'rgba(244, 67, 54, 0.1)', border: '1px solid rgba(244, 67, 54, 0.3)', borderRadius: '12px', color: '#f44336' }}>
                ⚠️ {error}
              </div>
            )}
          </div>
        </GameSetupScreen>
      </>
    )
  }

  // Network waiting (lobby with chat)
  if (gameMode === 'network' && phase === 'waiting') {
    const joinUrl = `${window.location.origin}/guesswho?join=${session?.session_code}`

    return (
      <GameLayout voice={voice}>
        {NotificationComponent}
        {ConfirmDialog}
        {AIQuestionDialog}

        <div className="arcade-lobby-screen">
          <GameHeader
            gameTitle="Guess Who?"
            gameIcon="🎭"
            players={[
              { number: 1, name: session?.player1_name || playerName, isConnected: true }
            ]}
            sessionCode={session?.session_code}
          />

          <div className="arcade-lobby-content">
            {/* Waiting Banner */}
            <div className="arcade-lobby-banner">
              <div className="lobby-icon">⏳</div>
              <h2>Waiting for Player 2...</h2>
              <p>Share the session code from the header to start playing!</p>

              <button
                className="btn btn-primary btn-lg"
                onClick={() => {
                  navigator.clipboard.writeText(joinUrl).then(() => {
                    showNotification('Invite link copied!', 'success')
                  }).catch(() => {
                    showNotification('Failed to copy URL', 'error')
                  })
                }}
              >
                📋 Copy Invite Link
              </button>
            </div>

            {/* Embedded Chat */}
            {session?.session_code && (
              <div className="arcade-lobby-chat">
                <ChatWidget
                  sessionCode={session.session_code}
                  playerNumber={playerNumber}
                  playerName={playerName}
                  otherPlayerName="Player 2"
                  sendMessage={(msg) => sendMove({ type: 'chat', message: msg })}
                  mode="embedded"
                />
              </div>
            )}
          </div>
        </div>
      </GameLayout>
    )
  }

  // Local/AI game playing
  if ((gameMode === 'local' || gameMode === 'ai') && phase === 'playing') {
    const currentCharacters = currentPlayer === 1 ? player1Characters : player2Characters
    const mysteryChar = currentPlayer === 1 ? player1Mystery : player2Mystery

    return (
      <div className="guess-who-container playing-game universal-game-room">
        {NotificationComponent}
        {ConfirmDialog}
        {AIQuestionDialog}
        {PlayerQuestionDialog}
        {QAHistoryPopup}

        {/* Universal Game Room Header */}
        <GameRoomHeader
          gameTitle="Guess Who?"
          gameIcon="🎭"
          mode={gameMode === 'ai' ? 'ai' : 'local'}
          players={[
            { name: player1Name, status: currentPlayer === 1 ? 'active' : 'thinking' },
            { name: player2Name, status: currentPlayer === 2 ? 'active' : 'thinking' }
          ]}
          onBack={() => {
            setPhase('setup')
            setGameMode('select')
          }}
        />

        {/* Mobile-Optimized Top Bar - Prominent Mystery Character */}
        <div className="game-top-bar-mobile">
          {/* Mystery Character - PROMINENT */}
          <div className="mystery-section-prominent">
            <span className="mystery-label-mobile">🎯 Your Mystery</span>
            {mysteryChar && (
              <>
                <div className="mystery-avatar-prominent">
                  <img src={generateAvatarUrl(mysteryChar)} alt={mysteryChar.name} />
                </div>
                <span className="mystery-name-prominent">{mysteryChar.name}</span>
              </>
            )}
          </div>

          {/* Status and Action Row */}
          <div className="status-actions-row">
            {/* Turn Indicator */}
            <div className="top-bar-section turn-section">
              <span className={`turn-indicator ${currentPlayer === 1 ? '' : 'waiting'}`}>
                {currentPlayer === 1 ? '🟢 Your Turn' : '⏳ AI Turn'}
              </span>
            </div>

            {/* Action Buttons - only for local mode */}
            {gameMode === 'local' && (
              <button
                onClick={switchTurn}
                className="btn-end-turn-compact"
              >
                ✓
              </button>
            )}
          </div>
        </div>

        {/* Q&A History Button - only in AI mode */}
        {gameMode === 'ai' && (
          <button
            className="qa-history-button"
            onClick={() => setShowQaHistory(true)}
            style={{ position: 'fixed', top: '80px', right: '20px', zIndex: 100 }}
          >
            📜 History ({qaHistory.length})
          </button>
        )}

        {/* Main content area with game board and sidebar */}
        <div className="game-room-content">
          {/* Game Board */}
          <div className="game-board-area">
            <div className="character-grid compact">
              {currentCharacters.map((character) => (
                <div
                  key={character.id}
                  className={`character-card compact ${character.eliminated ? 'eliminated' : ''}`}
                  onClick={() => eliminateCharacterLocal(character.id)}
                >
                  <div className="character-avatar">
                    <img src={generateAvatarUrl(character)} alt={character.name} loading="lazy" />
                  </div>
                  <h3>{character.name}</h3>
                  {!character.eliminated && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        makeGuessLocal(character.id)
                      }}
                      className="guess-button"
                      title="Make your final guess!"
                    >
                      🎯 Guess
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Universal Sidebar for AI mode */}
          {gameMode === 'ai' && (
            <GameRoomSidebar
              mode="ai"
              gameState={{
                turn: currentPlayer === 1 ? '🟢 Your Turn' : '⏳ AI Turn',
                phase: 'Playing'
              }}
              messages={chatMessages}
              historyItems={historyItems}
              onSendMessage={handleSidebarMessage}
              collapsed={sidebar.collapsed}
              onToggleCollapse={sidebar.toggle}
              position={sidebar.position}
              quickReactions={false}
            />
          )}
        </div>

        {/* Floating Action Button for mobile - only for local mode */}
        {gameMode === 'local' && (
          <FloatingActionButton
            icon="✓"
            label="End Turn"
            onClick={switchTurn}
            variant="primary"
          />
        )}
      </div>
    )
  }

  // Network game playing - with ChatWidget
  if (gameMode === 'network' && phase === 'playing') {
    const opponentName = playerNumber === 1 ? session?.player2_name : session?.player1_name
    const isMyTurn = currentTurn === playerNumber

    // Show loading state if characters not yet synced (Player 2 waiting for data)
    if (myCharacters.length === 0 || !mysteryCharacter) {
      return (
        <>
          {NotificationComponent}
          <div className="loading-screen" style={{ textAlign: 'center', padding: '40px' }}>
            <div className="loading-spinner" style={{ fontSize: '48px', marginBottom: '20px' }}>⏳</div>
            <h2>Loading game...</h2>
            <p style={{ color: 'var(--text-secondary)', marginTop: '10px' }}>
              Syncing game state from host...
            </p>
          </div>
        </>
      )
    }

    return (
      <div className="guess-who-container playing-game universal-game-room">
        {NotificationComponent}
        {ConfirmDialog}
        {AIQuestionDialog}

        {/* Universal Game Room Header */}
        <GameRoomHeader
          gameTitle="Guess Who?"
          gameIcon="🎭"
          mode="network"
          players={[
            {
              name: session?.player1_name || 'Player 1',
              status: currentTurn === 1 ? 'active' : 'thinking'
            },
            {
              name: session?.player2_name || 'Player 2',
              status: currentTurn === 2 ? 'active' : 'thinking'
            }
          ]}
          sessionCode={sessionCode}
          onBack={() => {
            setPhase('setup')
            setGameMode('select')
          }}
        />

        {/* Mobile-Optimized Top Bar - Prominent Mystery Character */}
        <div className="game-top-bar-mobile">
          {/* Mystery Character - PROMINENT */}
          <div className="mystery-section-prominent">
            <span className="mystery-label-mobile">🎯 Your Mystery</span>
            {mysteryCharacter && (
              <>
                <div className="mystery-avatar-prominent">
                  <img src={generateAvatarUrl(mysteryCharacter)} alt={mysteryCharacter.name} />
                </div>
                <span className="mystery-name-prominent">{mysteryCharacter.name}</span>
              </>
            )}
          </div>

          {/* Status Row */}
          <div className="status-actions-row">
            {/* Turn Indicator */}
            <div className="top-bar-section turn-section">
              <span className={`turn-indicator ${isMyTurn ? '' : 'waiting'}`}>
                {isMyTurn ? '🟢 Your Turn' : `⏳ ${opponentName}'s Turn`}
              </span>
            </div>

            {/* End Turn Button - Inline */}
            {isMyTurn && (
              <button onClick={switchTurnNetwork} className="btn-end-turn-compact">
                ✓
              </button>
            )}
          </div>
        </div>

        {/* Main content area with game board and sidebar */}
        <div className="game-room-content">
          {/* Game Board */}
          <div className="game-board-area">
            <div className="character-grid compact">
              {myCharacters.map((character) => (
                <div
                  key={character.id}
                  className={`character-card compact ${character.eliminated ? 'eliminated' : ''}`}
                  onClick={() => eliminateCharacterNetwork(character.id)}
                >
                  <div className="character-avatar">
                    <img src={generateAvatarUrl(character)} alt={character.name} loading="lazy" />
                  </div>
                  <h3>{character.name}</h3>
                  {!character.eliminated && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        makeGuessNetwork(character.id)
                      }}
                      className="guess-button"
                      title={isMyTurn ? 'Make final guess' : 'Wait for your turn'}
                      disabled={!isMyTurn}
                    >
                      🎯 Guess
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Universal Sidebar for network mode */}
          <GameRoomSidebar
            mode="network"
            gameState={{
              turn: isMyTurn ? '🟢 Your Turn' : `⏳ ${opponentName}'s Turn`,
              phase: 'Playing'
            }}
            messages={chatMessages}
            onSendMessage={handleSidebarMessage}
            collapsed={sidebar.collapsed}
            onToggleCollapse={sidebar.toggle}
            position={sidebar.position}
            quickReactions={true}
          />
        </div>

        {/* Voice Controls Footer */}
        <GameRoomFooter
          voice={{
            connectionStatus: voice.connectionStatus,
            voiceStatus: {
              localMuted: voice.isMuted,
              remoteMuted: false, // TODO: Track remote mute status
              localConnected: voice.isConnected,
              remoteConnected: voice.isConnected // Simplified for now
            },
            onToggleMute: voice.toggleMute,
            onConnect: voice.connect,
            onDisconnect: voice.disconnect
          }}
          mode="minimal"
          expandable={true}
        />

        {/* Floating Action Button for mobile */}
        {isMyTurn && (
          <FloatingActionButton
            icon="✓"
            label="End Turn"
            onClick={switchTurnNetwork}
            variant="primary"
          />
        )}
      </div>
    )
  }

  // Game finished
  if (phase === 'finished') {
    return (
      <>
        {NotificationComponent}
        {ConfirmDialog}
        {AIQuestionDialog}
        <GameSetupScreen
          gameTitle="Guess Who?"
          gameIcon="🎭"
          gameBanner="var(--gradient-purple)"
          supportedModes={[]}
          onModeSelect={() => {}}
        >
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <h1 style={{ fontSize: '48px', marginBottom: '20px' }}>🎉 Game Over!</h1>
            <button
              onClick={() => {
                setPhase('setup')
                setGameMode('select')
              }}
              className="btn btn-primary btn-lg"
            >
              Play Again
            </button>
          </div>
        </GameSetupScreen>
      </>
    )
  }

  return null
}
